import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";

/**
 * 全站錯誤邊界
 * ═══════════════════════════════════════════════════════════════
 *
 * 沒有這個東西的話，React 只要在 render 階段丟出任何例外，
 * 整棵樹會被卸載 —— 使用者看到的就是「畫面閃一下然後全白」，
 * 而且 console 以外沒有任何線索。
 *
 * 這個邊界處理兩類錯誤，做法不一樣：
 *
 *  1. 動態 chunk 載入失敗（Failed to fetch dynamically imported module）
 *     幾乎都是「部署後使用者的分頁還開著舊 HTML」造成的：
 *     舊 HTML 指向的 chunk 檔名已經不存在。
 *     → 自動重新載入一次（帶時間戳繞過快取）就會拿到新的 HTML。
 *     用 sessionStorage 記錄已經重載過，避免真的壞掉時無限重整。
 *
 *  2. 其他例外 → 顯示可讀的錯誤畫面，保留「回首頁」與「重新載入」。
 *     絕對不要靜靜地變成白畫面。
 */

const RELOAD_FLAG = "colouredev.chunk-reload";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // 部署後的舊分頁：重新載入就會拿到新版 HTML 與對應的 chunk
    if (isChunkLoadError(error) && !sessionStorage.getItem(RELOAD_FLAG)) {
      sessionStorage.setItem(RELOAD_FLAG, "1");
      window.location.reload();
      return;
    }

    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  componentDidMount() {
    // 成功掛載代表這次載入是好的，把重載旗標清掉
    sessionStorage.removeItem(RELOAD_FLAG);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const chunkError = isChunkLoadError(error);
    const copy = crashCopy();

    return (
      <div className="flex min-h-screen items-center justify-center bg-page px-6">
        <div className="w-full max-w-lg rounded-card nm p-8 text-center">
          <div className="mx-auto flex size-14 items-center justify-center rounded-2xl nm-inset text-2xl">
            {chunkError ? "🔄" : "⚠️"}
          </div>

          <h1 className="mt-5 text-xl font-semibold text-ink">
            {chunkError ? copy.chunkTitle : copy.title}
          </h1>

          <p className="mt-2 text-sm leading-relaxed text-ink-muted">
            {chunkError ? copy.chunkBody : copy.body}
          </p>

          {!chunkError && (
            <pre className="mt-5 max-h-40 overflow-auto rounded-xl nm-inset p-4 text-left text-xs leading-relaxed text-ink-soft">
              {error.message}
            </pre>
          )}

          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={() => {
                sessionStorage.removeItem(RELOAD_FLAG);
                window.location.reload();
              }}
              className="nm-pressable nm-brand rounded-pill px-6 py-2.5 text-sm font-semibold text-white"
            >
              {copy.reload}
            </button>
            <a
              href="/"
              className="nm-pressable rounded-pill px-6 py-2.5 text-sm font-medium text-ink"
            >
              {copy.home}
            </a>
          </div>
        </div>
      </div>
    );
  }
}

/**
 * 判斷是不是「動態 import 的 chunk 抓不到」。
 * 各家瀏覽器的訊息不一樣，所以要比對多個關鍵字。
 */
/**
 * 當機畫面的文案
 * ═══════════════════════════════════════════════════════════════
 *
 * 這一段刻意不走 i18n 字典，而是自己把每一種語言帶在身上。
 *
 * ErrorBoundary 包在最外層，I18nProvider 在它裡面 ——
 * 會走到這個畫面，正代表底下某個東西已經掛了，而那個東西
 * 完全可能就是 Provider 本身。當機畫面再去呼叫 useI18n()
 * 的話會第二次拋錯，使用者看到的就是全白。
 *
 * 所以語系直接從 localStorage 讀（和 I18nProvider 同一個 key），
 * 讀不到就退回瀏覽器語言。幾句話帶在身上的成本，
 * 遠低於「錯誤畫面自己也會壞」的風險。
 *
 * ── 這裡原本只有四種語言 ────────────────────────────────────
 *
 * 站上有八個語系，這張表卻只有 en / zh-TW / ja / es ——
 * 韓國、德國、法國、巴西的使用者當機時會拿到英文。
 *
 * 那個落差很難自己冒出來：i18n 稽核只看字典之間有沒有缺條目，
 * 而這張表刻意不是字典，所以稽核原本看不到它。
 * 現在 scripts/i18n-audit.mjs 會把這張表和字典目錄對一次 ——
 * 新增語系而忘了這裡，稽核就會紅。
 */
const CRASH_COPY = {
  en: {
    chunkTitle: "The site was just updated",
    title: "Something went wrong on this page",
    chunkBody: "You have an older version open — reload and you'll get the latest page.",
    body: "We've logged the error. You can reload, or go back to the home page.",
    reload: "Reload",
    home: "Home",
  },
  "zh-TW": {
    chunkTitle: "網站剛更新了",
    title: "這個頁面出了點問題",
    chunkBody: "你開著的是舊版本，重新載入就會拿到最新的頁面。",
    body: "我們已經記錄這個錯誤。你可以重新載入，或回到首頁繼續。",
    reload: "重新載入",
    home: "回首頁",
  },
  ja: {
    chunkTitle: "サイトが更新されました",
    title: "このページで問題が起きました",
    chunkBody: "古いバージョンを開いています。再読み込みすると最新のページになります。",
    body: "エラーは記録しました。再読み込みするか、トップページに戻ってお続けください。",
    reload: "再読み込み",
    home: "トップへ",
  },
  es: {
    chunkTitle: "La web se acaba de actualizar",
    title: "Algo ha fallado en esta página",
    chunkBody: "Tienes abierta una versión antigua: recarga y verás la página más reciente.",
    body: "Hemos registrado el error. Puedes recargar o volver a la página de inicio.",
    reload: "Recargar",
    home: "Inicio",
  },
  ko: {
    chunkTitle: "사이트가 방금 업데이트되었습니다",
    title: "이 페이지에서 문제가 생겼습니다",
    chunkBody: "예전 버전이 열려 있습니다. 새로고침하면 최신 페이지가 나옵니다.",
    body: "오류를 기록해 두었습니다. 새로고침하거나 첫 화면으로 돌아가셔도 됩니다.",
    reload: "새로고침",
    home: "홈으로",
  },
  de: {
    chunkTitle: "Die Seite wurde gerade aktualisiert",
    title: "Auf dieser Seite ist etwas schiefgegangen",
    chunkBody: "Sie haben eine ältere Version offen — laden Sie neu, dann bekommen Sie die aktuelle Seite.",
    body: "Wir haben den Fehler protokolliert. Sie können neu laden oder zur Startseite zurückgehen.",
    reload: "Neu laden",
    home: "Startseite",
  },
  fr: {
    chunkTitle: "Le site vient d'être mis à jour",
    title: "Un problème est survenu sur cette page",
    chunkBody: "Vous avez une ancienne version ouverte : rechargez pour obtenir la page la plus récente.",
    body: "Nous avons enregistré l'erreur. Vous pouvez recharger ou revenir à l'accueil.",
    reload: "Recharger",
    home: "Accueil",
  },
  "pt-BR": {
    chunkTitle: "O site acabou de ser atualizado",
    title: "Algo deu errado nesta página",
    chunkBody: "Você está com uma versão antiga aberta — recarregue para ver a página mais recente.",
    body: "Registramos o erro. Você pode recarregar ou voltar para a página inicial.",
    reload: "Recarregar",
    home: "Início",
  },
  it: {
    chunkTitle: "Il sito è stato appena aggiornato",
    title: "Si è verificato un problema su questa pagina",
    chunkBody: "Stai visualizzando una versione precedente: ricarica la pagina per accedere alla versione più recente.",
    body: "Abbiamo registrato l'errore. Puoi ricaricare la pagina o tornare alla home page.",
    reload: "Ricarica",
    home: "Home",
  },
  nl: {
    chunkTitle: "De site is zojuist bijgewerkt",
    title: "Er is iets misgegaan op deze pagina",
    chunkBody: "Je hebt een oudere versie openstaan — vernieuw de pagina om de nieuwste versie te zien.",
    body: "We hebben de fout geregistreerd. Je kunt de pagina vernieuwen of teruggaan naar de startpagina.",
    reload: "Vernieuwen",
    home: "Startpagina",
  },
  pl: {
    chunkTitle: "Strona została właśnie zaktualizowana",
    title: "Coś poszło nie tak na tej stronie",
    chunkBody: "Masz otwartą starszą wersję — odśwież stronę, aby pobrać najnowszą.",
    body: "Zapisaliśmy ten błąd. Możesz odświeżyć stronę lub wrócić do strony głównej.",
    reload: "Odśwież",
    home: "Strona główna",
  },
  tr: {
    chunkTitle: "Site az önce güncellendi",
    title: "Bu sayfada bir sorun oluştu",
    chunkBody: "Eski bir sürüm açık — yenilediğinizde en güncel sayfayı göreceksiniz.",
    body: "Hatayı kaydettik. Sayfayı yenileyebilir veya ana sayfaya dönebilirsiniz.",
    reload: "Yenile",
    home: "Ana Sayfa",
  },
  id: {
    chunkTitle: "Situs baru saja diperbarui",
    title: "Ada masalah pada halaman ini",
    chunkBody: "Anda membuka versi lama — muat ulang untuk mendapatkan halaman terbaru.",
    body: "Kami telah mencatat masalah ini. Anda dapat memuat ulang, atau kembali ke halaman utama.",
    reload: "Muat Ulang",
    home: "Beranda",
  },
  th: {
    chunkTitle: "เว็บไซต์เพิ่งได้รับการอัปเดต",
    title: "เกิดข้อผิดพลาดบางอย่างในหน้านี้",
    chunkBody: "คุณกำลังเปิดหน้าเว็บเวอร์ชันเก่าอยู่ กรุณารีโหลดเพื่อใช้งานเวอร์ชันล่าสุด",
    body: "ระบบได้บันทึกปัญหาที่เกิดขึ้นแล้ว คุณสามารถลองรีโหลด หรือกลับไปยังหน้าแรกได้",
    reload: "รีโหลด",
    home: "หน้าแรก",
  },
  vi: {
    chunkTitle: "Trang web vừa được cập nhật",
    title: "Đã xảy ra lỗi trên trang này",
    chunkBody: "Bạn đang mở phiên bản cũ — hãy tải lại để xem trang mới nhất.",
    body: "Chúng tôi đã ghi nhận lỗi này. Bạn có thể tải lại trang hoặc quay về trang chủ.",
    reload: "Tải lại",
    home: "Trang chủ",
  },
  ru: {
    chunkTitle: "Сайт только что обновился",
    title: "На этой странице что-то пошло не так",
    chunkBody: "У вас открыта устаревшая версия — обновите страницу, чтобы загрузить актуальную.",
    body: "Мы уже зафиксировали ошибку. Вы можете обновить страницу или вернуться на главную.",
    reload: "Обновить",
    home: "На главную",
  },
  ar: {
    chunkTitle: "تم تحديث الموقع للتو",
    title: "حدث خطأ ما في هذه الصفحة",
    chunkBody: "أنت تفتح إصدارًا قديمًا — أعد التحميل للحصول على أحدث صفحة.",
    body: "لقد سجلنا هذا الخطأ. يمكنك إعادة التحميل، أو العودة إلى الصفحة الرئيسية.",
    reload: "إعادة تحميل",
    home: "الرئيسية",
  },
  hi: {
    chunkTitle: "साइट अभी अपडेट हुई है",
    title: "इस पेज पर कुछ समस्या आ गई है",
    chunkBody: "आपके पास पुराना वर्ज़न खुला है — रीलोड करें और आपको नया पेज मिल जाएगा।",
    body: "हमने इस समस्या को दर्ज कर लिया है। आप रीलोड कर सकते हैं, या होम पेज पर वापस जा सकते हैं।",
    reload: "रीलोड करें",
    home: "होम",
  },
} as const;

function crashCopy() {
  const fallback = CRASH_COPY.en;
  if (typeof window === "undefined") return fallback;

  const candidates = [
    window.localStorage.getItem("colourdev.locale") ?? "",
    ...(navigator.languages ?? [navigator.language ?? ""]),
  ];

  for (const raw of candidates) {
    if (raw in CRASH_COPY) return CRASH_COPY[raw as keyof typeof CRASH_COPY];
    const base = raw.split("-")[0]?.toLowerCase();
    if (base === "zh") return CRASH_COPY["zh-TW"];
    if (base === "ja") return CRASH_COPY.ja;
    if (base === "es") return CRASH_COPY.es;
    if (base === "ko") return CRASH_COPY.ko;
    if (base === "de") return CRASH_COPY.de;
    if (base === "fr") return CRASH_COPY.fr;
    if (base === "pt") return CRASH_COPY["pt-BR"];
    if (base === "en") return CRASH_COPY.en;
  }
  return fallback;
}

function isChunkLoadError(error: Error): boolean {
  const message = `${error.name} ${error.message}`.toLowerCase();
  return (
    message.includes("dynamically imported module") ||
    message.includes("failed to fetch dynamically") ||
    message.includes("importing a module script failed") ||
    message.includes("chunkloaderror") ||
    message.includes("loading chunk")
  );
}
